import type { JSX, ReactNode } from 'react';
import {
  IconChevronRight,
  IconChip,
  IconHome,
  IconPlus,
  IconStar2,
  IconStore,
  IconTimesheet,
  cn,
} from '@taki/ui';
import type { ShellAppEntry } from '../types.js';

/** Nombre d'apps recentes affichees. La maquette (E5) en montre trois. */
const RECENT_APPS_COUNT = 3;

export interface QuickLauncherProps {
  /** Ouvert (rendu) ou non. */
  open: boolean;
  /** Ferme le lanceur. */
  onClose: () => void;
  /** Apps installees (dernieres utilisees en tete, c.f. leaves). */
  apps: readonly ShellAppEntry[];
  /** Selection d'une app : navigue vers /app/$appId. */
  onPickApp: (appId: string) => void;
  /** Ouvre le panneau Ask Taki AI. */
  onAskAI: () => void;
  /** Retour au bureau (liste complete des apps), ferme le lanceur. */
  onHome: () => void;
}

/** Carre 52px de la rangee du bas (creation rapide, Ask AI). */
const squareButton = cn(
  'relative flex h-[3.25rem] w-[3.25rem] flex-shrink-0 items-center justify-center rounded-[0.875rem]',
  'border border-border bg-surface text-ink-600',
  'transition-all duration-150 hover:border-border-strong hover:bg-surface-1',
);

/** Petit badge lime (creation rapide) ancre en haut-droite d'un carre. */
function PlusBadge(): JSX.Element {
  return (
    <span
      className={cn(
        'absolute -right-[0.3125rem] -top-[0.3125rem] flex h-[1.0625rem] w-[1.0625rem] items-center justify-center',
        'rounded-pill border-[1.5px] border-surface bg-accent text-accent-ink',
      )}
    >
      <IconPlus size={9} />
    </span>
  );
}

/** Carre de creation rapide : icone d'app + badge lime. */
function QuickCreateButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" aria-label={label} onClick={onClick} className={squareButton}>
      {icon}
      <PlusBadge />
    </button>
  );
}

/**
 * Lanceur rapide (overlay sur l'app courante), maquette Spot Motion E5.
 *
 * Voile translucide flou, panneau blanc flottant de 400px, titre "LAST USED
 * APPS", les TROIS dernieres apps utilisees, puis une rangee : Home (large),
 * deux carres de creation rapide a badge lime, et le carre lime Ask Taki AI.
 *
 * Rien si `open` est faux. Le notch et le positionnement plein-ecran sont geres
 * par le shell : ici on remplit un wrapper inset-0 en flux normal.
 */
export function QuickLauncher({
  open,
  onClose,
  apps,
  onPickApp,
  onAskAI,
  onHome,
}: QuickLauncherProps): JSX.Element | null {
  if (!open) return null;

  /** Selectionne une app puis ferme le lanceur. */
  const pick = (appId: string): void => {
    onPickApp(appId);
    onClose();
  };

  const recentApps = apps.slice(0, RECENT_APPS_COUNT);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      {/* Voile : token translucide dedie (bg-scrim), jamais `bg-bg/xx`. */}
      <button
        type="button"
        aria-label="Fermer le lanceur"
        onClick={onClose}
        className="absolute inset-0 bg-scrim backdrop-blur-sm"
      />

      <div
        role="dialog"
        aria-label="Lanceur rapide"
        className={cn(
          'relative z-10 flex w-[25rem] flex-col',
          'rounded-2xl border border-border bg-surface p-4 shadow-panel',
        )}
      >
        <div className="px-3 pb-3 pt-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-300">
          Last used apps
        </div>

        <div className="flex flex-col gap-0.5">
          {recentApps.map((app) => (
            <button
              key={app.id}
              type="button"
              onClick={() => pick(app.id)}
              className={cn(
                'flex items-center gap-3.5 rounded-[0.875rem] px-3 py-[0.6875rem] text-left',
                'transition-all duration-[120ms] hover:bg-surface-1',
              )}
            >
              <IconChip size={40}>{app.icon}</IconChip>
              <span className="flex-1 truncate text-base font-semibold text-ink-900">
                {app.label}
              </span>
              <IconChevronRight size={17} className="text-ink-300" />
            </button>
          ))}
        </div>

        {/* Respiration avant la rangee d'actions (maquette : spacer de 14px). */}
        <div className="min-h-[0.875rem] flex-1" />

        {/* Rangee d'actions : Home large, creations rapides, CTA lime Ask AI. */}
        <div className="flex items-stretch gap-2.5">
          <button
            type="button"
            onClick={onHome}
            className={cn(
              'flex h-[3.25rem] flex-1 items-center gap-3 rounded-[0.875rem] px-[1.125rem]',
              'border border-border bg-surface',
              'transition-all duration-150 hover:border-border-strong hover:bg-surface-1',
            )}
          >
            <IconHome size={21} className="text-ink-600" />
            <span className="text-[0.9375rem] font-semibold text-ink-900">Home</span>
          </button>

          <QuickCreateButton
            label="Nouvelle app depuis l'App Store"
            icon={<IconStore size={21} />}
            onClick={onClose}
          />
          <QuickCreateButton
            label="Nouvelle saisie de temps"
            icon={<IconTimesheet size={21} />}
            onClick={onClose}
          />

          <button
            type="button"
            aria-label="Ask Taki AI"
            onClick={onAskAI}
            className={cn(
              'flex h-[3.25rem] w-[3.25rem] flex-shrink-0 items-center justify-center rounded-[0.875rem]',
              'border border-accent-border bg-accent text-accent-ink',
              'transition-all duration-150 hover:bg-accent-hover hover:shadow-accent',
            )}
          >
            <IconStar2 size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
